import {coordsToKey} from "../coords/coordsToKey";
import {getAllCityHexKeys} from "./getAllCityHexKeys";
import {isHexTokenIsBlockerForMovement} from "../movement/isHexTokenIsBlockerForMovement";
import {HEX_DIRECTIONS} from "../../constants/geometry";
import {INCOME_INFLUENCE_PATH_LENGTH} from "../../configs/income";

/**
 * Рассчитать доход от гильдии по формуле:
 * 1 + {количество городов противников, до которых можно провести маршрут
 * по непустым ячейкам (но не по mountain и не по sea) длиной до 5 ячеек}
 *
 * @param {string} guildHexKey - координаты гильдии
 * @param {Array} enemyCities - массив городов противников
 * @param {Object} hexes - объект всех ячеек карты
 * @returns {{count: number, paths: Array<Array<string>>}} доход от данной гильдии и пути влияния
 */
export const calculateInfluencePathIncome = (guildHexKey, enemyCities, hexes) => {
  const visited = new Set();
  const parents = new Map();
  const [startQ, startR] = guildHexKey.split(',').map(Number);

  // BFS очередь: каждая запись [q, r, currentDistance]
  const queue = [[startQ, startR, 0]];
  visited.add(guildHexKey);

  const reachableEnemyCities = new Set();
  const cityPaths = new Map();

  // Собираем все хексы городов противников для быстрого поиска
  const enemyCityHexMap = new Map();
  for (const city of enemyCities) {
    const cityHexes = getAllCityHexKeys(city);
    for (const hexKey of cityHexes) {
      enemyCityHexMap.set(hexKey, city);
    }
  }

  while (queue.length > 0) {
    const [currentQ, currentR, currentDistance] = queue.shift();
    const currentKey = coordsToKey(currentQ, currentR);

    // Проверяем является ли эта ячейка частью города противника
    if (enemyCityHexMap.has(currentKey)) {
      const city = enemyCityHexMap.get(currentKey);
      if (!reachableEnemyCities.has(city)) {
        reachableEnemyCities.add(city);
        // Восстанавливаем путь от гильдии до текущей ячейки
        const path = [currentKey];
        let node = currentKey;
        while (node !== guildHexKey) {
          node = parents.get(node);
          if (!node) break;
          path.unshift(node);
        }
        cityPaths.set(city.centralQuarter, path);
      }
    }

    // Если достигли максимальной дистанции - не идем дальше
    if (currentDistance >= INCOME_INFLUENCE_PATH_LENGTH) {
      continue;
    }

    // Обходим всех соседей
    for (const [dq, dr] of HEX_DIRECTIONS) {
      const newQ = currentQ + dq;
      const newR = currentR + dr;
      const newKey = coordsToKey(newQ, newR);

      // Пропускаем если уже посещали
      if (visited.has(newKey)) {
        continue;
      }

      // Проверяем существует ли ячейка
      if (!hexes[newKey]) {
        continue;
      }

      const hexToken = hexes[newKey];
      if (isHexTokenIsBlockerForMovement(hexToken)) {
        continue;
      }

      // Для путей влияния мы ходим только по непустым ячейкам (есть токен)
      if (!hexToken) {
        continue;
      }

      // Ячейка подходит, добавляем в очередь
      visited.add(newKey);
      parents.set(newKey, currentKey);
      queue.push([newQ, newR, currentDistance + 1]);
    }
  }

  // Формула дохода за пути влияния: количество доступных городов противника
  return {
    count: reachableEnemyCities.size,
    paths: Array.from(cityPaths.values()),
  };
};
